import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import { formatCurrencyInCents, formatDate } from '../../helpers/formatters';
import MoneyInputWithBlur from '../MoneyInputWithBlur';

/** Alinha com TransactionService::PAYMENT_METHODS (API). */
const PAYMENT_METHOD_OPTIONS = [
    { value: 'pix', label: 'PIX' },
    { value: 'credit_card', label: 'Cartão de Crédito' },
    { value: 'debit_card', label: 'Cartão de Débito' },
    { value: 'bank_transfer', label: 'Transferência Bancária' },
    { value: 'cash', label: 'Dinheiro' },
    { value: 'other', label: 'Outro / Permuta' }
];

const API_PAYMENT_METHODS = new Set(PAYMENT_METHOD_OPTIONS.map((o) => o.value));

/**
 * Pedidos guardam `payment_methods[].name` em PT (ex.: Crédito, PIX).
 * Converte para o código esperado pela API.
 */
function orderPaymentNameToApiCode(raw) {
    if (raw == null || raw === '') {
        return 'pix';
    }
    if (typeof raw === 'string' && API_PAYMENT_METHODS.has(raw.trim())) {
        return raw.trim();
    }
    const key = String(raw)
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const map = {
        debito: 'debit_card',
        credito: 'credit_card',
        pix: 'pix',
        dinheiro: 'cash',
        transferencia: 'bank_transfer',
        permuta: 'other'
    };
    return map[key] ?? 'other';
}

const PaymentReceiptModal = ({ open, onClose, order }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        type: 'income',
        amount: 0,
        deduction: 0,
        net_amount: 0,
        description: '',
        payment_method: 'pix',
        authorization_id: '',
        payment_date: null,
    });
    const [errors, setErrors] = useState({});

    // Preencher dados quando o modal abrir
    React.useEffect(() => {
        if (open && order) {
            const orderAmount = order.amount / 100; // Converter de centavos para reais

            let paymentMethod = 'pix';
            if (order.payment_methods && order.payment_methods.length > 0) {
                const firstPayment = order.payment_methods[0];
                if (firstPayment.name) {
                    paymentMethod = orderPaymentNameToApiCode(firstPayment.name);
                }
            }

            const orderDate = order.created_at ? new Date(order.created_at) : new Date();
            const paymentDate = Number.isNaN(orderDate.getTime()) ? new Date() : orderDate;

            setFormData({
                type: 'income',
                amount: orderAmount,
                deduction: 0,
                net_amount: orderAmount,
                description: '',
                payment_method: paymentMethod,
                authorization_id: '',
                payment_date: paymentDate,
            });
        }
    }, [open, order]);

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: value };

            // Calcular deduction automaticamente baseado em amount e net_amount
            if (field === 'amount' || field === 'net_amount') {
                const amount = field === 'amount' ? (parseFloat(value) || 0) : newData.amount;
                const netAmount = field === 'net_amount' ? (parseFloat(value) || 0) : newData.net_amount;

                // Deduction = Amount - NetAmount
                const deduction = amount - netAmount;
                newData.deduction = deduction;
            }

            return newData;
        });

        // Limpar erro do campo quando o usuário começar a digitar
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.type) {
            newErrors.type = 'Tipo é obrigatório';
        }

        if (!formData.amount || formData.amount <= 0) {
            newErrors.amount = 'Valor é obrigatório e deve ser maior que zero';
        }

        if (!formData.payment_method) {
            newErrors.payment_method = 'Método de pagamento é obrigatório';
        }

        if (!formData.payment_date || Number.isNaN(formData.payment_date.getTime?.() ?? NaN)) {
            newErrors.payment_date = 'Data do pagamento é obrigatória';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const transactionData = {
                type: formData.type,
                amount: Math.round(formData.amount * 100), // Converter para centavos
                deduction: Math.round(formData.deduction * 100),
                net_amount: Math.round(formData.net_amount * 100),
                description: formData.description.trim(),
                payment_method: formData.payment_method,
                order_id: order.id,
                authorization_id: formData.authorization_id.trim(),
                created_at: formData.payment_date.toISOString(),
            };

            const response = await api.post('transactions', transactionData);

            if (response.data.success) {
                // Transação já vincula o pedido; conciliação vem de `payment_received` na listagem (EXISTS em transações).
                onClose(true);

                // Mostrar sucesso após fechar o modal
                setTimeout(() => {
                    Swal.fire({
                        title: 'Sucesso!',
                        text: 'Recebimento registrado com sucesso!',
                        icon: 'success',
                        confirmButtonText: 'OK'
                    });
                }, 100);
            } else {
                if (response.data.errors) {
                    setErrors(response.data.errors);
                } else {
                    throw new Error(response.data.message || 'Erro ao registrar recebimento');
                }
            }
        } catch (error) {
            console.error('Erro ao registrar recebimento:', error);
            await Swal.fire({
                title: 'Erro!',
                text: error.message || 'Erro ao registrar recebimento',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose(false);
        }
    };

    if (!order) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h5" component="div">
                    💰 Registrar Recebimento
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Pedido: {order.id} | Cliente: {order.customer?.name || 'N/A'}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {/* Informações do Pedido */}
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Informações do Pedido:
                            </Typography>
                            <Typography variant="body2">
                                • Valor Total: {formatCurrencyInCents(order.amount)}
                            </Typography>
                            <Typography variant="body2">
                                • Canal de Venda: {order.sales_channel?.name || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                                • Data: {formatDate(order.created_at, false)}
                            </Typography>
                        </Alert>
                    </Grid>

                    {/* Método de Pagamento */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Método de Pagamento"
                            value={formData.payment_method}
                            onChange={(e) => handleInputChange('payment_method', e.target.value)}
                            error={!!errors.payment_method}
                            helperText={errors.payment_method}
                        >
                            {PAYMENT_METHOD_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Data do pagamento (vira created_at da transação) */}
                    <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                            <DatePicker
                                label="Data do pagamento"
                                value={formData.payment_date}
                                onChange={(newDate) => handleInputChange('payment_date', newDate)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!errors.payment_date,
                                        helperText: errors.payment_date
                                            || 'Use a data do extrato/comprovante para bater na conciliação.',
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Valor (Amount) */}
                    <Grid item xs={12} md={6}>
                        <MoneyInputWithBlur
                            label="Valor"
                            value={formData.amount}
                            onChange={(values) => handleInputChange('amount', values.value)}
                        />
                        {errors.amount && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                {errors.amount}
                            </Typography>
                        )}
                    </Grid>

                    {/* Dedução (calculada automaticamente) */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Dedução"
                            value={formData.deduction.toFixed(2)}
                            InputProps={{
                                readOnly: true,
                                style: {
                                    fontSize: '1rem'
                                }
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    backgroundColor: 'action.disabledBackground',
                                    color: 'text.primary'
                                }
                            }}
                        />
                    </Grid>

                    {/* Valor Líquido */}
                    <Grid item xs={12} md={6}>
                        <MoneyInputWithBlur
                            label="Valor Líquido"
                            value={formData.net_amount}
                            onChange={(values) => handleInputChange('net_amount', values.value)}
                        />
                    </Grid>

                    {/* Descrição */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Código de autorização bancária"
                            value={formData.authorization_id}
                            onChange={(e) => handleInputChange('authorization_id', e.target.value)}
                            helperText="Opcional. Ex.: ID da transação no extrato ou comprovante do banco/maquininha."
                            placeholder="Ex.: SOURCE_ID, NSU, código PIX"
                            inputProps={{ maxLength: 64 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Descrição"
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            helperText="Descreva o recebimento (opcional)"
                            placeholder="Ex: Recebimento via PIX, Pagamento em dinheiro, etc."
                        />
                    </Grid>

                    {/* Resumo dos Valores */}
                    {formData.amount > 0 && (
                        <Grid item xs={12}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Resumo do Recebimento:
                                </Typography>
                                <Typography variant="body2">
                                    • Valor: {formatCurrencyInCents(Math.round(formData.amount * 100))}
                                </Typography>
                                <Typography variant="body2">
                                    • Dedução: {formatCurrencyInCents(Math.round(formData.deduction * 100))} (calculada automaticamente)
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    • Valor Líquido: {formatCurrencyInCents(Math.round(formData.net_amount * 100))}
                                </Typography>
                                {formData.payment_date && (
                                    <Typography variant="body2">
                                        • Data do pagamento: {formatDate(formData.payment_date.toISOString(), false)}
                                    </Typography>
                                )}
                                {formData.authorization_id.trim() && (
                                    <Typography variant="body2">
                                        • Autorização: {formData.authorization_id.trim()}
                                    </Typography>
                                )}
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    variant="outlined"
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    variant="contained"
                    color="success"
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? 'Registrando...' : 'Registrar Recebimento'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentReceiptModal;
